// Issue a client-portal link for a buyer's (hidden) file and email them a link
// to their buyer forms — usable BEFORE they've signed. Mirrors the send flow in
// ClientPortalPanel.handleSendEmail, but with buyer-appropriate copy and no
// deal-detail context. Best-effort: returns { ok, reason?, url?, demo? } and
// never throws for expected conditions (no email, no backend, etc.).
import { issuePortal, IS_DEV, makeDemoPortal } from "./clientPortal";
import { getContactsByIds } from "./firebasefunctions";
import { sendTransactionEmail } from "./emailSender";
import { plainTextToHtml, wrapBrandedEmail } from "./emailBrandTemplate";
import { buildButton } from "./openHouseEmailsHtml";
import { recordSentEmail } from "./sentEmails";
import { listClients } from "./leadClients";
import { emailsForContacts, splitToBcc, pickEmail, pickName } from "./emailFanout";

export async function sendBuyerPortalForms(deal, lead, user = {}) {
  const dealId = String(deal?.createdat || deal?.id || "");
  const clients = listClients(lead);
  const contactIds = clients.map((c) => c.contactId).filter(Boolean);
  if (!dealId || !contactIds.length)
    return { ok: false, reason: "missing file or contact" };

  // ONE magic link, seeded with the primary — the portal gate unlocks for any
  // co-client's last name, so every client shares this link.
  const issued = await issuePortal({ dealId, contactId: contactIds[0] });
  const portal = issued.ok || !IS_DEV ? issued : makeDemoPortal(dealId);
  if (!portal.ok) return { ok: false, reason: "could not create portal link" };

  // Resolve EVERY client's email — the forms go to all of them, not just one.
  let contacts = [];
  try {
    contacts = (await getContactsByIds(contactIds)) || [];
  } catch (_) {
    /* fall through to lead fields */
  }
  let emails = emailsForContacts(contacts);
  if (!emails.length && pickEmail(lead)) emails = [pickEmail(lead)];
  const { to, bcc, all } = splitToBcc(emails);
  const toName =
    pickName(contacts[0]) || clients[0]?.contactName || lead?.contactName || "";
  if (!to) return { ok: false, reason: "no email on file", url: portal.url };

  // Sandbox: the link is synthetic, so don't fire a real email at a fake token.
  if (IS_DEV) return { ok: true, url: portal.url, demo: true };

  const firstName = toName.split(" ")[0] || "there";
  const subject = "Your buyer forms — Team Edelstein";
  const intro =
    `Hi ${firstName},\n\n` +
    `Welcome! To get started, we've put together a quick set of online forms for you — a few details about what you're looking for and the best ways to reach you. They only take a few minutes, and your answers save as you go.`;
  const outro = `Looking forward to working with you!\nTeam Edelstein`;
  const text = `${intro}\n\nOpen your secure link here:\n${portal.url}\n\n${outro}`;
  const html = wrapBrandedEmail({
    contentHtml:
      plainTextToHtml(intro) +
      `<p style="margin:18px 0;">${buildButton(portal.url, "Open Your Buyer Forms")}</p>` +
      plainTextToHtml(outro),
    sender: user,
    propertyAddress: "",
    videoCard: "",
  });

  const res = await sendTransactionEmail({
    userId: user?.id,
    to,
    toName,
    bcc,
    subject,
    text,
    html,
    emailId: "buyer-portal-forms",
    relatedDealId: dealId,
  });
  if (!res?.ok)
    return { ok: false, reason: res?.error || "send failed", url: portal.url };

  try {
    await recordSentEmail({
      dealId,
      dealAddress: "",
      emailId: "buyer-portal-forms",
      sequenceSide: "Buyer",
      agentId: user?.id || null,
      agentName: user?.displayname || "",
      recipientEmail: all.join(", "),
      recipientName: toName,
      subject,
      bodyHtml: html,
      attachments: [],
      videoUrl: "",
      gmailMessageId: res.gmailMessageId || null,
    });
  } catch (e) {
    console.error("sent-email history write failed (email DID send):", e);
  }
  return { ok: true, url: portal.url, recipients: all };
}
